import { compact, uniq } from 'lodash-es'
import { action, computed, observable, runInAction } from 'mobx'
import {
  asapScheduler,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  type Observable,
  startWith,
  subscribeOn,
  switchMap,
} from 'rxjs'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type {
  AdminDocumentsQuery,
  DocumentsSortField,
  SearchDocumentsQuery,
  DocumentStore,
} from '@/features/documents/DocumentStore'
import { type Document } from '@/features/documents/Document'
import { type Job } from '@/features/jobs/Job'
import { InfiniteSelectFilter } from '@/infra/InfiniteSelectFilter'
import { SelectFilter } from '@/infra/SelectFilter'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorage,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import {
  CursorPaged,
  type CursorPageQuery,
  type Direction,
} from '@/infra/paging'
import type { FetchJobsFn, SearchJobsFn } from '@/features/jobs'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { deepEqual } from '@/utils/object-util'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import { tapAction, toStream } from '@/utils/mobx-util'
import { type DocumentTypeStore } from '@/features/documents/DocumentTypeStore'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Document search view model.
 */
export class DocumentSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Jobs linked to the searched documents, by ID.
   */
  private readonly _linkedJobs = observable.map<Id, Job>()

  /**
   * Backing field for the last search.
   */
  @observable.ref private accessor _lastSearch: DocumentSearchResult = {
    documents: [],
    searchFilter: DocumentSearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Total documents from the last search.
   */
  @observable
  accessor totalDocuments = 0

  /**
   * Sorting.
   */
  @observable
  accessor sorting: DocumentSort = {
    field: undefined,
    direction: undefined,
  }

  /**
   * Search text.
   */
  readonly searchText = new SearchTextViewModel()

  readonly filters

  /**
   * Documents scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    Document,
    DocumentSearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading documents failed',
        signal,
      )

      return withRetry(() =>
        this._fetchDocuments(
          toSearchDocumentsQuery(this.userPublicId, paging, filter),
          signal,
        ),
      )
    },
  })

  /**
   * Search filter changes.
   */
  private readonly _searchFilter$

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  readonly searches$

  constructor(
    private readonly _userPublicId: () => Id,
    private readonly _getAvailableWorkspaces: () => Workspace[],
    private readonly _documentTypeStore: DocumentTypeStore,
    private readonly _searchJobs: SearchJobsFn,
    private readonly _documentStore: DocumentStore,
    private readonly _fetchJobs: FetchJobsFn,
    private readonly _filterStorage: FilterStorage,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.filters = new FiltersViewModel<DocumentFilter>({
      filterStorage: this._filterStorage,
      definitions: {
        workspaces: {
          title: 'Workspaces',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(this._getWorkspaces()),
              true,
            ),
        },
        jobs: {
          icon: 'jobs',
          title: 'Jobs',
          factory: (key, title) =>
            new InfiniteSelectFilter(
              key,
              title,
              async ({ paging, filter }) =>
                this._fetchSearchableJobs(paging, filter),
              true,
            ),
        },
        documentTypes: {
          icon: 'documents',
          title: 'Document types',
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              async () => this._fetchDocumentTypes(),
              true,
            ),
        },
        archived: {
          icon: 'delete',
          title: 'Archived',
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static([
                { value: true, title: 'Archived only' },
                { value: false, title: 'Unarchived only' },
              ]),
              false,
            ),
        },
      },
    })

    this._searchFilter$ = combineLatest([
      // Combine the search text changes
      this.searchText.debouncedValue$,
      // ...with the remaining filter changes
      this.filters.appliedFilters$,
      // ...and the sorting changes.
      toStream(() => this.sorting, true),
    ]).pipe(
      // Map what we've combined to a Search Filter.
      map(([searchText, appliedFilters, sort]): DocumentSearchFilter => {
        return {
          sort,
          searchText: searchText ?? '',
          workspaceIds: appliedFilters.workspaces,
          jobIds: appliedFilters.jobs,
          documentTypeIds: appliedFilters.documentTypes,
          archived: appliedFilters.archived?.slice().pop(),
        }
      }),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    this.searches$ = this._searchFilter$.pipe(
      // Start a non-filtered search immediately.
      startWith(DocumentSearchFilter.none),
      // Ignore emissions with the same search filter.
      distinctUntilChanged<DocumentSearchFilter>(DocumentSearchFilter.equals),
      // Start a search, return the filter that was used.
      switchMap((query): Observable<DocumentSearchResult> =>
        withAbortSignal(async (signal) => {
          let result = await this.scroller.fetch({ filter: query, signal })
          // Keep loading more until we get something back that isn't being filtered out
          // on the client. If we don't do this here, we potentially display an empty state
          // which does not trigger infinite scroll.
          while (this.documents.length === 0 && !this.scroller.isDone) {
            signal.throwIfAborted()
            result = await this.scroller.more(signal)
          }

          return {
            searchFilter: query,
            filtersApplied: !DocumentSearchFilter.isNone(query),
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- a reported total of 0 falls back to the loaded count
            total: result?.total || this.documents.length,
            documents: this.documents,
          }
        }).pipe(
          // The retry alert reported the failure; the next filter searches
          // again.
          catchError(() => EMPTY),
        ),
      ),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    this._lifecycle.manage(this.filters).manage(
      this.searches$.pipe(
        tapAction((search) => {
          this._lastSearch = search
          this.totalDocuments = search.total
        }),
        // The tracker subscribes before it activates the filters; waiting for
        // them makes the first search use the stored filters.
        subscribeOn(asapScheduler),
      ),
    )
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The internal documents list ran through the transformer.
   * Honors the applied archived filter so documents archived after
   * being fetched drop out of the list reactively.
   */
  @computed
  get documents() {
    const documents = uniq(this.scroller.items)
    const archived = this.lastSearch.searchFilter.archived

    // When the archived filter isn't being used, return all documents
    if (archived === undefined) {
      return documents
    }

    // Otherwise, filter out documents not matching the archived filter
    // that's include the documents archived when the filter is implemented
    return documents.filter((doc) => doc.archived === archived)
  }

  /**
   * The currently selected user.
   */
  @computed
  get userPublicId() {
    return this._userPublicId()
  }

  /**
   * Last search computed getter.
   */
  @computed
  get lastSearch() {
    return this._lastSearch
  }

  /**
   * Gets the workspaces filter.
   */
  @computed
  get workspacesFilter() {
    return this.filters.getFilter('workspaces') as SelectFilter<string, Id>
  }

  /**
   * The values of the archived filter.
   */
  @computed
  get archivedFilterValue() {
    const filter = this.filters.getFilter('archived') as
      SelectFilter<'archived', boolean | undefined> | undefined
    return filter?.value.slice()
  }

  /**
   * The loaded jobs the document is linked to.
   */
  linkedJobs(document: Document) {
    return compact(document.jobIds.map((id) => this._linkedJobs.get(id)))
  }

  /**
   * Sets the document sort field.
   */
  @action.bound
  setSortField(field?: DocumentsSortField) {
    this.sorting = {
      field,
      direction: this.sorting.direction,
    }
  }

  /**
   * Sets the document sort direction.
   */
  @action.bound
  setSortDirection(direction?: Direction) {
    if (!direction) {
      this.setSortField(undefined)
      return
    }

    this.sorting = {
      field: this.sorting.field,
      direction,
    }
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Fetch documents.
   *
   * @param params
   */
  private async _fetchDocuments(
    params: SearchDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    const page = await this._documentStore.searchDocuments(
      { ...params },
      signal,
    )

    const jobIds = uniq(
      page.data
        .map((doc) => doc.links.job.jobIds.map((jobId) => Id.assert(jobId)))
        .flat(),
    )

    const withRetry = this._createAlertRetryWrapper(
      'Loading linked jobs failed',
      signal,
    )
    withRetry(() => this._fetchJobs(jobIds, signal))
      .then((jobs) =>
        runInAction(() => {
          jobs.forEach((job) => this._linkedJobs.set(job.id, job))
        }),
      )
      // The retry alert has reported the failure.
      .catch(() => undefined)

    return page
  }

  /**
   * Get available workspaces.
   */
  private _getWorkspaces(): FilterItem<Id>[] {
    return this._getAvailableWorkspaces().map((w) => {
      return {
        title: w.name,
        value: w.id,
      }
    })
  }

  /**
   * Fetch searchable jobs.
   */
  private _fetchSearchableJobs(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ) {
    const searchText = filter?.searchText as string | undefined

    return this._searchJobs({
      ...paging,
      searchText,
      memberIds: [this.userPublicId],
      scope: 'All',
    }).then((jobs): CursorPaged<FilterItem<Id>> =>
      CursorPaged.mapT(jobs, (job) => ({
        title: job.displayName,
        description: job.legendName,
        value: Id(job.id),
      })),
    )
  }

  /**
   * Fetch document types.
   */
  private async _fetchDocumentTypes(): Promise<FilterItem<Id>[]> {
    return this._documentTypeStore.listTypes().then((dt) =>
      dt.map((dt) => {
        return {
          title: dt.name,
          description: dt.shortHelpText,
          value: dt.id,
        }
      }),
    )
  }
}

/**
 * Document search result.
 */
export interface DocumentSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: DocumentSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of documents contained in the searcher.
   */
  readonly documents: readonly Document[]
}

export interface DocumentSort {
  field?: DocumentsSortField
  direction?: Direction
}

/**
 * Document filter.
 */
export interface DocumentFilter {
  workspaces: Id[]
  jobs: Id[]
  documentTypes: Id[]
  archived?: (boolean | undefined)[]
}

/**
 * Document search filter.
 */
export interface DocumentSearchFilter {
  searchText?: string
  workspaceIds?: Id[] | null
  jobIds?: Id[] | null
  documentTypeIds?: Id[] | null
  archived?: boolean
  sort: DocumentSort
}

export const DocumentSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspaceIds: [],
    jobIds: [],
    documentTypeIds: [],
    archived: undefined,
    sort: {
      field: undefined,
      direction: undefined,
    },
  } as DocumentSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: DocumentSearchFilter) =>
    DocumentSearchFilter.equals(DocumentSearchFilter.none, searchFilter),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<DocumentSearchFilter>,
    right: Partial<DocumentSearchFilter>,
  ) => deepEqual(left, right),
}

/**
 * Creates a `SearchDocumentsQuery` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toSearchDocumentsQuery(
  userPublicId: Id,
  paging: CursorPageQuery,
  filter: DocumentSearchFilter | undefined,
): SearchDocumentsQuery {
  const query: AdminDocumentsQuery = {
    context: 'Admin',
    searchText: filter?.searchText || undefined,
    workspaceIds: filter?.workspaceIds ?? undefined,
    ownerPublicIds: [userPublicId],
    jobIds: filter?.jobIds ?? undefined,
    documentTypeIds: filter?.documentTypeIds ?? undefined,
    archived: filter?.archived,
    limit: paging.limit,
    after: paging.after,
  }

  if (filter?.sort.field && filter?.sort.direction) {
    query.orderBy = {
      field: filter.sort.field,
      direction: filter.sort.direction,
    }
  }

  return query
}
